import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { DataGrid } from '../DataGrid';
import { errorsFormatter } from './formatters';
import { batches as batchesApi } from '@afosto/content/api/Batches';

const BatchErrorsGrid = ({ batchId, shouldReloadErrors, onReloadErrors }) => {
  const intl = useIntl();

  const fetchErrors = async ({ pagination = {} }) => {
    try {
      const {
        activePage,
        itemsPerPage,
        getPageCursor,
        setNextPageCursor,
        setItemCount,
      } = pagination;
      const options = {
        headers: {
          'x-page-size': itemsPerPage,
        },
      };

      if (activePage || activePage === 0) {
        const pageCursor = getPageCursor(activePage);

        if (pageCursor) {
          options.headers = {
            ...options.headers,
            'x-page': pageCursor,
          };
        } else {
          options.headers = {
            ...options.headers,
            'x-page': activePage + 1,
          };
        }
      }

      const response = await batchesApi.getBatchErrors(batchId, options);
      const { data = [] } = response;

      const itemCount = response.headers['x-count'];
      const pageCursor = response.headers['x-page'];

      if (pageCursor) {
        setNextPageCursor(pageCursor);
      }

      if (itemCount) {
        setItemCount(itemCount);
      }

      return data;
    } catch (err) {
      return [];
    }
  };

  const dataSource = useDataGrid(fetchErrors, {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'reference',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batchErrors.reference',
            defaultMessage: 'Reference',
            description: 'The column name for column reference',
          }),
        },
        {
          key: 'column',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batchErrors.column',
            defaultMessage: 'Column',
            description: 'The column name for column',
          }),
        },
        {
          key: 'message',
          name: intl.formatMessage({
            id: 'dataGrid.columns.batchErrors.message',
            defaultMessage: 'Message',
            description: 'The column name for column message',
          }),
        },
      ],
    },
  });
  const dataSourceRef = useRef(dataSource);
  const onReloadErrorsRef = useRef(onReloadErrors);

  useEffect(() => {
    dataSourceRef.current = dataSource;
    onReloadErrorsRef.current = onReloadErrors;
  });

  useEffect(() => {
    if (shouldReloadErrors) {
      const { current: currentDataSource } = dataSourceRef;
      const { current: onReload } = onReloadErrorsRef;

      currentDataSource.reloadDataSource();

      if (onReload && typeof onReload === 'function') {
        onReload();
      }
    }
  }, [shouldReloadErrors]);

  return <DataGrid dataSource={dataSource} formatters={{ errors: errorsFormatter }} />;
};

BatchErrorsGrid.propTypes = {
  batchId: PropTypes.string.isRequired,
  shouldReloadErrors: PropTypes.bool,
  onReloadErrors: PropTypes.func,
};

BatchErrorsGrid.defaultProps = {
  shouldReloadErrors: false,
  onReloadErrors: undefined,
};

export default BatchErrorsGrid;
